import {useMemo} from "react";
import {CoverImagesItemResponse} from "../../../react-query-hooks/products/types";
import {mapEmbeddedToDomain} from "../data/mappers/media.mapper.v2";
import {CoverImage, OfferingType} from "../domain";
import {useGetCoverImages} from "./useMedia";

/**
 * Options for useCoverImagesWithFallback hook
 */
export interface UseCoverImagesOptions {
  offeringType: Exclude<OfferingType, "portfolio">;
  offeringId?: string | number;
  embedded?: CoverImagesItemResponse[];
}

/**
 * Data source for cover images
 */
export type CoverImagesSource = "v2" | "embedded" | "none";

/**
 * Result from useCoverImagesWithFallback hook
 */
export interface UseCoverImagesResult {
  coverImages: CoverImage[];
  isLoading: boolean;
  isError: boolean;
  source: CoverImagesSource;
}

/**
 * Hook that fetches cover images with automatic fallback to embedded data.
 *
 * When offeringId is provided:
 * - Fetches from v2 API via useGetCoverImages
 * - Falls back to embedded data if the v2 query fails or returns nothing
 *
 * When no offeringId is provided:
 * - Uses embedded data directly (mapped to domain type)
 *
 * @param options - Offering type, ID, and optional embedded fallback data
 * @returns Cover images array with loading/error state and data source indicator
 */
export function useCoverImagesWithFallback(
  options: UseCoverImagesOptions
): UseCoverImagesResult {
  const {offeringType, offeringId, embedded} = options;

  // v2 query only runs when offeringId is provided
  const v2Query = useGetCoverImages(offeringType, offeringId);

  // Map embedded data to domain format (memoized)
  const mappedEmbedded = useMemo(() => mapEmbeddedToDomain(embedded), [embedded]);

  // Determine result based on state
  return useMemo(() => {
    // valid offeringId provided
    if (offeringId !== undefined) {
      // Still loading
      if (v2Query.isLoading) {
        return {
          coverImages: [],
          isLoading: true,
          isError: false,
          source: "none" as const,
        };
      }

      // v2 data available and has images
      if (v2Query.data && v2Query.data.length > 0 && !v2Query.isError) {
        return {
          coverImages: v2Query.data,
          isLoading: false,
          isError: false,
          source: "v2" as const,
        };
      }

      // v2 returned empty or error - fall back to embedded if available
      if (mappedEmbedded.length > 0) {
        return {
          coverImages: mappedEmbedded,
          isLoading: false,
          isError: false,
          source: "embedded" as const,
        };
      }

      // v2 has data (empty array) and no embedded - return v2 empty
      if (v2Query.data && !v2Query.isError) {
        return {
          coverImages: v2Query.data,
          isLoading: false,
          isError: false,
          source: "v2" as const,
        };
      }

      // No v2 data and no embedded - return empty
      return {
        coverImages: [],
        isLoading: false,
        isError: false,
        source: "none" as const,
      };
    }

    // no offeringId - use embedded directly
    if (mappedEmbedded.length > 0) {
      return {
        coverImages: mappedEmbedded,
        isLoading: false,
        isError: false,
        source: "embedded" as const,
      };
    }

    // No data at all
    return {
      coverImages: [],
      isLoading: false,
      isError: false,
      source: "none" as const,
    };
  }, [offeringId, v2Query.isLoading, v2Query.data, v2Query.isError, mappedEmbedded]);
}
